import type { ApiRoutes } from '@hana/data';
import { IconAnchor, IconHome, IconBooks, IconPackage, IconReceipt2, IconShoppingCart, IconSpeakerphone, IconStack2, IconTargetArrow, IconTruck, IconUsers } from '@tabler/icons-react';
import type { NavGroup, Zone } from '@hana/shell';
import type { EzyModule } from './module';

// Vùng HANA Ezy (/ezy). Mỗi module (thư mục) tự khai `module.ts`; file này TỰ QUÉT `./*/module.ts`, KHÔNG sửa khi thêm màn.
const found = import.meta.glob<EzyModule>('./*/module.ts', { eager: true, import: 'default' });
const dirOf = (k: string) => k.replace(/^\.\//, '').replace(/\/module\.ts$/, '');
const modules = Object.keys(found)
  .map(dirOf)
  .sort()
  .map((d) => found[`./${d}/module.ts`])
  .filter((m): m is EzyModule => !!m);

// Menu theo board (Mint-Job NAVK.cl): cụm CRM · NGHIỆP VỤ · TÀI CHÍNH · NỘI BỘ, nhóm + nhãn + thứ tự đúng board. Module vẫn khai mục menu của mình (kể cả cổng quyền tiền);
// chỗ này chỉ xếp lại: mục `free` luôn hiện (route có sẵn), mục còn lại chỉ hiện khi module khai (money / ghế).
type Spec = { key: string; label: string; icon: NavGroup['icon']; items: [string, string, boolean?][] };
const BOARD: { section: string; groups: Spec[] }[] = [
  { section: 'CRM', groups: [
    { key: 'kh', label: 'Khách hàng', icon: IconUsers, items: [['Khách hàng', '/ezy/customers', true], ['Đối tác', '/ezy/partners/YANGMING', true], ['Hợp đồng dịch vụ', '/ezy/contracts', true]] },
    { key: 'kd', label: 'Bán hàng', icon: IconTargetArrow, items: [['Hôm nay của Sale', '/ezy/today', true], ['Cơ hội', '/ezy/pipeline', true], ['Báo giá', '/ezy/quotes', true]] },
    { key: 'mk', label: 'Marketing', icon: IconSpeakerphone, items: [['Marketing & quà', '/ezy/marketing', true], ['Market Share', '/ezy/market-share', true]] },
  ] },
  { section: 'NGHIỆP VỤ', groups: [
    { key: 'gia', label: 'Giá & lịch tàu', icon: IconAnchor, items: [['Giá và Lịch', '/ezy'], ['Freight Cost', '/ezy/rates'], ['Services Map', '/ezy/services'], ['Hỏi giá đối tác', '/ezy/inquiry'], ['Thiết lập giá', '/ezy/setup'], ['Tin thị trường', '/ezy/news']] },
    { key: 'job', label: 'Job', icon: IconPackage, items: [['Danh sách Job', '/ezy/jobs', true], ['Booking', '/ezy/booking', true], ['Chứng từ & B/L', '/ezy/bl', true], ['Hàng nhập & cont', '/ezy/import', true]] },
    { key: 'dv', label: 'Dịch vụ', icon: IconStack2, items: [['Service Hub', '/ezy/service-tasks', true], ['Tra HS', '/ezy/hs', true], ['Customs Data', '/ezy/customs-data', true], ['Danh mục dịch vụ', '/ezy/service-catalog', true], ['Biểu giá dịch vụ', '/ezy/service-price', true], ['Định mức', '/ezy/service-norms', true], ['GP theo dịch vụ', '/ezy/service-gp']] },
    { key: 'vt', label: 'Vận tải', icon: IconTruck, items: [['Điều độ', '/ezy/tms'], ['Lưới điều xe', '/ezy/tms/dispatch'], ['Bản đồ điểm & tuyến', '/ezy/tms/places'], ['Tuyến', '/ezy/tms/routes'], ['Tính giá TRF', '/ezy/tms/routes/price', true], ['Đội xe & danh mục', '/ezy/tms/fleet']] },
  ] },
  { section: 'TÀI CHÍNH', groups: [
    { key: 'ban', label: 'Bán ra', icon: IconReceipt2, items: [['Hoá đơn', '/ezy/invoice'], ['Công nợ', '/ezy/ar'], ['Thanh toán', '/ezy/payments']] },
    { key: 'mua', label: 'Mua vào', icon: IconShoppingCart, items: [['Chi phí & Costing', '/ezy/cost'], ['Phải trả', '/ezy/pay'], ['Chi phí theo mảng', '/ezy/opscost'], ['Quyết toán', '/ezy/settle']] },
  ] },
  { section: 'NỘI BỘ', groups: [
    { key: 'tv', label: 'Thư viện', icon: IconBooks, items: [['Tài liệu & media', '/ezy/files', true], ['SOP & hướng dẫn', '/ezy/sop', true], ['Tiện ích', '/ezy/utility', true]] },
  ] },
];
const declared = new Set(modules.flatMap((m) => (m.nav ? m.nav.items.map((i) => i.to) : [])));
const nav: NavGroup[] = BOARD.flatMap(({ section, groups }) => groups.map((g, gi) => ({
  key: g.key, label: g.label, icon: g.icon, section: gi === 0 ? section : undefined,
  items: g.items.filter(([, to, free]) => free || declared.has(to)).map(([label, to]) => ({ key: to, label, to })),
})).filter((g) => g.items.length));

export const zone: Zone = {
  key: 'ezy',
  bare: ['/ezy/files/view'],
  name: 'HANA Ezy',
  top: [{ key: 'home', label: 'Trang chủ', to: '/ezy/home', icon: IconHome }],
  nav,
  routes: (parent) => modules.flatMap((m) => m.routes?.(parent) ?? []),
  fixtures: (register) => modules.forEach((m) => m.fixtures?.(register)),
};

/** route `api` của các module Ezy (Hub gom vào `API_ROUTES`) */
export const apiRoutes: ApiRoutes = Object.assign({}, ...modules.map((m) => m.api ?? {}));
